import { memo, useMemo } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, TableBody, TableCell, TableRow, useTheme } from "@mui/material";
import { CustomClassData, PClassEnum, CClassEnum } from "types";
import { IResultItem, RawSubValItem } from "../../types";
import { P_CLASS_SYMBOLS } from "../../common/consts";
import { getBorderClassUnit } from "../../common/utils";
import Parameter from "../Parameter";
import getStyles from "./styles";

type Props = {
  side: "left" | "right";
  options: IResultItem[];
  borderClass: PClassEnum | CClassEnum | undefined;
  customBorderClass: CustomClassData | undefined;
  isBorderClassP: boolean;
  isBorderClassC: boolean;
};

const BorderArea: FC<Props> = ({ side, options, borderClass, customBorderClass, isBorderClassP, isBorderClassC }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).BorderArea, [theme]);

  const { pCustomBorderClass, cCustomBorderClass, borderClassParams } = useMemo(() => {
    const pCustom = customBorderClass?.class_type === "P" ? customBorderClass : undefined;
    const cCustom = customBorderClass?.class_type === "C" ? customBorderClass : undefined;

    return {
      pCustomBorderClass: pCustom,
      cCustomBorderClass: cCustom,
      borderClassParams: {
        borderClass,
        isBorderClassP,
        isBorderClassC,
        pCustomBorderClass: pCustom,
        cCustomBorderClass: cCustom,
      },
    };
  }, [borderClass, customBorderClass, isBorderClassC, isBorderClassP]);

  const borderAreaLabel = useMemo(() => t(side === "left" ? "Border area left" : "Border area right"), [side, t]);

  const borderAreaClassLabel = useMemo(
    () => (customBorderClass?.class_type ? `${customBorderClass.class_type} ${t("Custom")}` : borderClass),
    [borderClass, customBorderClass?.class_type, t]
  );

  return (
    <TableBody>
      <TableRow sx={styles.head}>
        <TableCell>
          {borderAreaLabel} <strong>{borderAreaClassLabel}</strong>
        </TableCell>
        {options.map((lum, index) => {
          const borderAreaStandard = side === "left" ? lum.borderAreaLeftStandard : lum.borderAreaRightStandard;
          const isMet = borderAreaStandard.length > 0 && borderAreaStandard === "met";
          const isNotMet = borderAreaStandard.length > 0 && borderAreaStandard === "notmet";
          const isTooHigh = borderAreaStandard.length > 0 && (borderAreaStandard === "toohight" || borderAreaStandard === "toohigh");
          return (
            <TableCell key={"lum11" + index} align="center" scope="row">
              <Box
                component="strong"
                sx={{ color: isMet ? "var(--color-emerald-dark)" : isNotMet ? "var(--color-red)" : isTooHigh ? "var(--color-orange-dark)" : "inherit" }}
              >
                {isMet ? t("met") : isNotMet ? t("not met") : isTooHigh ? t("too high") : ""}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
      {P_CLASS_SYMBOLS.map((symbol, index) => (
        <TableRow key={"lum36" + index}>
          <TableCell>
            <Parameter label={symbol.htmlSymbol} unit={getBorderClassUnit(symbol.symbol, borderClassParams)} />
          </TableCell>
          {options.map((lum, index) => (
            <TableCell key={`lum37${index}`} align="center" scope="row">
              {/*@ts-ignore */}
              {lum.result.subVal
                ?.filter((obj: { name: string }) => obj.name === `sd-fpath-${side}`)
                .map((filtObj: RawSubValItem, i: number) => (
                  <span key={`sd-fpath-${side}${i}`}>{(filtObj[symbol.symbol as keyof RawSubValItem] as number).toFixed(2)}</span>
                ))}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
};

BorderArea.displayName = "BorderArea";

export default memo(BorderArea);
